import { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { ADMIN_COLORS } from '../constants/bookingConst';
import { formatTimeSlotRange, getCourtLabel, spinnerRing } from '../utils/bookingUtils';
import { useConfig } from '../context/ConfigContext';
import {
  LOCK_STATES,
  fetchSlotLockGroups,
  deleteSlotLock,
  deleteSlotLocksForDate,
  deleteExpiredSlotLocksForDate,
} from '../utils/slotLockAdminUtil';
import Modal from './Modal';
import '../css/AdminSlotLockPanel.css';

const COLORS = ADMIN_COLORS;

/**
 * How each lock state reads to an operator, and the colour it wears.
 *
 * `hint` is the part that matters: the screen exists so someone can decide
 * whether a lock is safe to remove, and that decision is different for each
 * state. Colour is a second cue only — every pill is labelled.
 */
const STATE_META = {
  [LOCK_STATES.ACTIVE]: {
    label: 'Holding',
    color: COLORS.success,
    hint: 'A guest is in checkout right now. Removing this frees their slot for someone else.',
  },
  [LOCK_STATES.EXPIRED]: {
    label: 'Expired',
    color: COLORS.warning,
    hint: 'The payment window has passed. The next guest may claim it — safe to remove.',
  },
  [LOCK_STATES.PERMANENT]: {
    label: 'Reserved',
    color: COLORS.inkSoft,
    hint: 'A submitted booking holds this. Cancel the booking instead of deleting the lock.',
  },
};

/** "Sat, 26 Sep 2026" from a "yyyy-MM-dd" key, falling back to the raw key. */
const formatDateHeading = (dateStr) => {
  const parsed = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateStr;
  return parsed.toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
};

/** "in 4 min" / "12 min ago" / "no expiry" for a lock's deadline. */
const formatExpiry = (expiryMs, nowMs) => {
  if (expiryMs === null) return 'no expiry';
  const diff = expiryMs - nowMs;
  const mins = Math.round(Math.abs(diff) / 60000);
  const unit = mins === 1 ? 'min' : 'mins';
  if (Math.abs(diff) >= 86400000) {
    const days = Math.round(Math.abs(diff) / 86400000);
    return diff > 0 ? `in ${days}d` : `${days}d ago`;
  }
  return diff > 0 ? `in ${mins} ${unit}` : `${mins} ${unit} ago`;
};

/**
 * Slot locks, listed and clearable.
 *
 * A slot lock is the hold placed on a court-hour while a guest is paying.
 * It normally comes off by itself — released when they finish or cancel, or
 * taken over by the next guest once its payment window expires. When one
 * gets stuck, that court-hour is unbookable by ANYONE and nothing in the
 * booking screens explains why, which is what this panel is for.
 *
 * Lives on Error Logs because that screen is already restricted to config
 * admins (see AdminSidebar's `configAdminOnly` and AdminPage's isConfigAdmin
 * gate) and is where someone already goes when the site is misbehaving.
 *
 * @returns {JSX.Element}
 */
function AdminSlotLockPanel() {
  const { config } = useConfig();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed read and "no locks at all" must not look the same: one means
  // nothing is stuck, the other means we cannot tell.
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState('');
  const [expandedDate, setExpandedDate] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [notice, setNotice] = useState(null);
  // Frozen per load so every row on screen is judged against the same
  // instant — rows re-rendering at different `Date.now()`s made a lock flip
  // between Holding and Expired mid-list.
  const [nowMs, setNowMs] = useState(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    const at = Date.now();
    try {
      const next = await fetchSlotLockGroups(db, at);
      setGroups(next);
      setNowMs(at);
      setLoadError(null);
    } catch (error) {
      setLoadError(error?.message || String(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => groups.reduce((acc, group) => {
    group.locks.forEach((lock) => { acc[lock.state] = (acc[lock.state] || 0) + 1; });
    acc.total += group.locks.length;
    return acc;
  }, { total: 0 }), [groups]);

  /**
   * Runs a delete, then reloads so the list reflects what is actually in the
   * database rather than an optimistic guess.
   * @param {string} message - shown while it runs.
   * @param {() => Promise<number>} work - resolves with the number removed.
   */
  const runDelete = useCallback(async (message, work) => {
    setConfirm(null);
    setBusy(message);
    try {
      const removed = await work();
      await load();
      setNotice({
        type: removed > 0 ? 'success' : 'info',
        title: removed > 0 ? 'Slot locks cleared' : 'Nothing to clear',
        message: removed > 0
          ? `${removed} slot lock${removed === 1 ? '' : 's'} removed. Those court-hours are bookable again.`
          : 'Those locks were already gone — the list has been refreshed.',
      });
    } catch (error) {
      setNotice({
        type: 'error',
        title: 'Could not clear the locks',
        message: `${error?.message || String(error)}\n\nIf this says permission denied, the database rules may need deploying.`,
      });
    } finally {
      setBusy('');
    }
  }, [load]);

  const askDeleteLock = (lock) => setConfirm({
    title: 'Remove this slot lock?',
    message: `${getCourtLabel(config, lock.court)} · ${formatTimeSlotRange(lock.timeSlot)} on ${formatDateHeading(lock.date)}.\n\n${STATE_META[lock.state]?.hint || ''}`,
    confirmText: 'Remove lock',
    onConfirm: () => runDelete('Removing lock…', () => deleteSlotLock(db, lock)),
  });

  const askDeleteExpired = (group) => setConfirm({
    title: 'Clear expired locks?',
    message: `Removes ${group.counts[LOCK_STATES.EXPIRED]} expired lock(s) on ${formatDateHeading(group.date)}. Locks still holding, and those behind submitted bookings, are left alone.`,
    confirmText: 'Clear expired',
    onConfirm: () => runDelete('Clearing expired locks…', () => deleteExpiredSlotLocksForDate(db, group.date, Date.now())),
  });

  const askDeleteDate = (group) => setConfirm({
    title: 'Clear every lock on this date?',
    message: `Removes all ${group.locks.length} lock(s) on ${formatDateHeading(group.date)}, including any guest currently in checkout and any submitted booking's hold.\n\nOnly do this when you already know the bookings behind them are gone.`,
    confirmText: 'Clear all',
    onConfirm: () => runDelete('Clearing locks…', () => deleteSlotLocksForDate(db, group.date)),
  });

  return (
    <div className="aslp">
      <div className="aslp-header">
        <div>
          <h3 className="aslp-title">
            Slot locks
            <span className="aslp-count">{totals.total}</span>
          </h3>
          <p className="aslp-sub">
            Holds placed on a court-hour while a guest pays. A stuck lock makes that hour unbookable
            for everyone — clear it here.
          </p>
        </div>
        <button type="button" className="aslp-btn" onClick={load} disabled={loading || Boolean(busy)}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {totals.total > 0 && (
        <div className="aslp-legend">
          {Object.entries(STATE_META).map(([state, meta]) => (
            <span className="aslp-legend-item" key={state}>
              <span className="aslp-dot" style={{ background: meta.color }} />
              <span className="aslp-legend-label">{meta.label}</span>
              <span className="aslp-legend-count">{totals[state] || 0}</span>
            </span>
          ))}
        </div>
      )}

      {busy ? (
        <div className="aslp-busy">{spinnerRing(busy)}</div>
      ) : loading ? (
        <div className="aslp-busy">{spinnerRing('Reading slot locks…')}</div>
      ) : loadError ? (
        <p className="aslp-error">Could not read slot locks: {loadError}</p>
      ) : groups.length === 0 ? (
        <p className="aslp-empty">No slot locks are held right now — nothing is blocking a booking.</p>
      ) : (
        <div className="aslp-list">
          {groups.map((group) => {
            const expanded = expandedDate === group.date;
            const expiredCount = group.counts[LOCK_STATES.EXPIRED] || 0;
            return (
              <div className={`aslp-group${expanded ? ' is-open' : ''}`} key={group.date}>
                <button
                  type="button"
                  className="aslp-group-head"
                  onClick={() => setExpandedDate(prev => (prev === group.date ? null : group.date))}
                  aria-expanded={expanded}
                >
                  <span className="aslp-caret" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
                  <span className="aslp-group-date">{formatDateHeading(group.date)}</span>
                  <span className="aslp-group-pills">
                    {Object.entries(STATE_META).map(([state, meta]) => (
                      group.counts[state] ? (
                        <span
                          className="aslp-pill"
                          key={state}
                          style={{ color: meta.color, borderColor: meta.color }}
                        >
                          {group.counts[state]} {meta.label}
                        </span>
                      ) : null
                    ))}
                  </span>
                </button>

                {expanded && (
                  <div className="aslp-group-body">
                    <div className="aslp-rows">
                      {group.locks.map((lock) => {
                        const meta = STATE_META[lock.state];
                        return (
                          <div className="aslp-row" key={`${lock.timeSlot}__${lock.court}`}>
                            <span className="aslp-court">{getCourtLabel(config, lock.court)}</span>
                            <span className="aslp-time">{formatTimeSlotRange(lock.timeSlot)}</span>
                            <span className="aslp-state" style={{ color: meta.color }}>
                              <span className="aslp-dot" style={{ background: meta.color }} />
                              {meta.label}
                            </span>
                            <span className="aslp-expiry">{formatExpiry(lock.expiryMs, nowMs)}</span>
                            <code className="aslp-booking" title={lock.bookingId}>{lock.bookingId || '—'}</code>
                            <button
                              type="button"
                              className="aslp-remove"
                              aria-label={`Remove lock on ${getCourtLabel(config, lock.court)} at ${formatTimeSlotRange(lock.timeSlot)}`}
                              onClick={() => askDeleteLock(lock)}
                            >&times;</button>
                          </div>
                        );
                      })}
                    </div>

                    <div className="aslp-group-actions">
                      <button
                        type="button"
                        className="aslp-btn"
                        onClick={() => askDeleteExpired(group)}
                        disabled={expiredCount === 0}
                      >
                        Clear expired ({expiredCount})
                      </button>
                      <button type="button" className="aslp-btn aslp-btn-danger" onClick={() => askDeleteDate(group)}>
                        Clear all on this date
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={Boolean(confirm)}
        type="warning"
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        confirmText={confirm?.confirmText || 'Confirm'}
        showCancel
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />

      <Modal
        isOpen={Boolean(notice)}
        type={notice?.type || 'info'}
        title={notice?.title || ''}
        message={notice?.message || ''}
        onClose={() => setNotice(null)}
      />
    </div>
  );
}

export default AdminSlotLockPanel;
